import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useMissingTimesheets, usePayPeriods } from '@/hooks/use-payroll';
import type { MissingTimesheetTech } from '@/hooks/use-payroll';

function SeverityBadge({ missing, draft, total }: { missing: number; draft: number; total: number }) {
  if (total === 0) return null;
  const outstanding = missing + draft;
  if (outstanding === 0) {
    return <Badge className="bg-green-100 text-green-800">All submitted</Badge>;
  }
  const ratio = outstanding / total;
  if (ratio < 0.25) {
    return <Badge className="bg-yellow-100 text-yellow-800">{outstanding} outstanding</Badge>;
  }
  return <Badge className="bg-red-100 text-red-800">{outstanding} outstanding</Badge>;
}

function TechRow({ tech }: { tech: MissingTimesheetTech }) {
  return (
    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{tech.person_name ?? tech.tech_name ?? 'Unknown'}</p>
        <p className="text-sm text-gray-600">{tech.project_name}</p>
      </div>
      <Button variant="outline" size="sm" disabled title="Coming in S09">
        Send Reminder
      </Button>
    </div>
  );
}

function DraftTechRow({ tech }: { tech: MissingTimesheetTech }) {
  return (
    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{tech.person_name ?? tech.tech_name ?? 'Unknown'}</p>
        <p className="text-sm text-gray-600">{tech.project_name}</p>
      </div>
      <Badge className="bg-yellow-100 text-yellow-800">Draft only</Badge>
    </div>
  );
}

export function MissingTimesheetsWidget() {
  const { data: payPeriods, isLoading: periodsLoading } = usePayPeriods({ status: 'open' });
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | undefined>(undefined);

  // Auto-select the first open pay period
  const activePeriodId = selectedPeriodId ?? payPeriods?.[0]?.id;

  const { data, isLoading } = useMissingTimesheets(activePeriodId);

  if (periodsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Missing Timesheets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!payPeriods || payPeriods.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Missing Timesheets
          </CardTitle>
          {payPeriods.length > 1 && (
            <Select
              value={String(activePeriodId ?? '')}
              onValueChange={(v) => setSelectedPeriodId(Number(v))}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {payPeriods.map((pp) => (
                  <SelectItem key={pp.id} value={String(pp.id)}>
                    {pp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : data ? (
          <div className="space-y-4">
            {/* Summary row */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700">
                  {data.summary.submitted_count} of {data.summary.total_expected} expected techs have submitted
                </span>
              </div>
              <SeverityBadge missing={data.summary.missing_count} draft={data.summary.draft_count} total={data.summary.total_expected} />
            </div>

            {/* Status breakdown */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-lg font-bold text-green-700">{data.summary.submitted_count}</p>
                  <p className="text-xs text-green-600">Submitted</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg">
                <Clock className="h-4 w-4 text-yellow-600" />
                <div>
                  <p className="text-lg font-bold text-yellow-700">{data.summary.draft_count}</p>
                  <p className="text-xs text-yellow-600">Draft only</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <div>
                  <p className="text-lg font-bold text-red-700">{data.summary.missing_count}</p>
                  <p className="text-xs text-red-600">Missing</p>
                </div>
              </div>
            </div>

            {/* Missing techs list */}
            {data.missing_techs.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Missing submissions</h4>
                {data.missing_techs.map((tech) => (
                  <TechRow key={tech.person_id} tech={tech} />
                ))}
              </div>
            )}

            {/* Draft techs list */}
            {data.draft_techs.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Draft only (not submitted)</h4>
                {data.draft_techs.map((tech) => (
                  <DraftTechRow key={tech.person_id} tech={tech} />
                ))}
              </div>
            )}

            {data.summary.missing_count === 0 && data.summary.draft_count === 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-sm text-green-700 font-medium">
                  All expected techs have submitted timesheets for this period.
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No data available.</p>
        )}
      </CardContent>
    </Card>
  );
}
